import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/Card';
import FileDropzone from '../../../components/FileDropzone';
import quoteDocumentsService from '../../../services/quoteDocuments.service';
import { saveAs } from '../../../utils/saveFile';
import type { QuoteDocumentRow } from '../../../types/quoteDocuments';
import QuoteDocPreviewModal from '../components/docs/QuoteDocPreviewModal';
import QuoteDocEditDialog from '../components/docs/QuoteDocEditDialog';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';

interface Props {
  quoteNumber?: number;
  revisionNumber?: number;
}

const FALLBACK_DOC_TYPES = [
  'CONTRACT', 'RATE CARD', 'BID DOCUMENT', 'TARIFF', 'AUTHORIZATION', 'INSURANCE', 'OTHER',
];

/** Reads `err.response.data.detail` for 4xx errors, else a generic message. */
function createErrorMessage(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  const status = resp?.status;
  const detail = resp?.data?.detail;
  if (status >= 400 && status < 500 && typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
}

/** Confirm-before-delete modal, portaled to <body> so it overlays the drawer. */
const ConfirmDeleteModal: React.FC<{
  filename: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ filename, busy, onConfirm, onCancel }) => {
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);
  return createPortal(
    <div
      role="dialog"
      aria-label="Delete document"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-6"
      onClick={busy ? undefined : onCancel}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-[400px] rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title="Delete Document"
          onClose={busy ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 py-4 text-[0.78125rem] text-slate-700 dark:text-slate-200">
          Delete <span className="font-semibold">{filename}</span>? This can't be undone here.
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button type="button" onClick={onCancel} disabled={busy} className="rounded border border-slate-300 bg-panel px-3 py-1.5 text-[0.75rem] text-slate-600 hover:bg-slate-50 dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={busy} className="rounded border border-red-700 bg-red-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-red-700 disabled:opacity-40">
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const DocumentsTab: React.FC<Props> = ({ quoteNumber, revisionNumber }) => {
  const saved = quoteNumber != null && revisionNumber != null;

  const [rows, setRows] = useState<QuoteDocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [docTypes, setDocTypes] = useState<string[]>(FALLBACK_DOC_TYPES);
  const [selectedType, setSelectedType] = useState<string>(FALLBACK_DOC_TYPES[0]);
  const [description, setDescription] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Preview modal / edit dialog / delete confirm + type filter.
  const [previewing, setPreviewing] = useState<QuoteDocumentRow | null>(null);
  const [editing, setEditing] = useState<QuoteDocumentRow | null>(null);
  const [deleting, setDeleting] = useState<QuoteDocumentRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Distinct types present, for the filter dropdown.
  const presentTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.documentType).filter(Boolean))).sort(),
    [rows],
  );
  const visibleRows = useMemo(
    () => (typeFilter ? rows.filter((r) => r.documentType === typeFilter) : rows),
    [rows, typeFilter],
  );

  // Keep the latest metadata in refs so the (stable) onUpload closure handed to
  // FileDropzone always reads the CURRENT type/description, never a stale capture.
  const typeRef = useRef(selectedType);
  const descRef = useRef(description);
  useEffect(() => { typeRef.current = selectedType; }, [selectedType]);
  useEffect(() => { descRef.current = description; }, [description]);

  const refreshList = useCallback(() => {
    if (quoteNumber == null || revisionNumber == null) return;
    let cancelled = false;
    setLoading(true);
    setListError(null);
    quoteDocumentsService.list(quoteNumber, revisionNumber)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((e: unknown) => {
        if (!cancelled) setListError(createErrorMessage(e, 'Failed to load documents.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteNumber, revisionNumber]);

  // Load document types once on mount.
  useEffect(() => {
    let cancelled = false;
    quoteDocumentsService.documentTypes()
      .then((types) => {
        if (cancelled || !types || types.length === 0) return;
        setDocTypes(types);
        setSelectedType(types[0]);
      })
      .catch(() => { /* keep fallback list */ });
    return () => { cancelled = true; };
  }, []);

  // Load the document list on mount / when the quote changes.
  useEffect(() => {
    if (!saved) return;
    return refreshList();
  }, [saved, refreshList]);

  const onUpload = useCallback(
    (file: File): Promise<QuoteDocumentRow | null> => {
      if (quoteNumber == null || revisionNumber == null) return Promise.resolve(null);
      setUploadError(null);
      return quoteDocumentsService
        .upload(quoteNumber, revisionNumber, file, {
          documenttype: typeRef.current,
          description: descRef.current || undefined,
        })
        .catch((e: unknown) => {
          setUploadError(createErrorMessage(e, `Upload failed for ${file.name}.`));
          throw e;
        });
    },
    [quoteNumber, revisionNumber],
  );

  const handleDownload = useCallback(async (row: QuoteDocumentRow) => {
    if (quoteNumber == null || revisionNumber == null) return;
    try {
      await saveAs(row.filename || 'document', () =>
        quoteDocumentsService.downloadBlob(quoteNumber, revisionNumber, row.mapRecno),
      );
    } catch (e) {
      setUploadError(createErrorMessage(e, `Failed to download ${row.filename}.`));
    }
  }, [quoteNumber, revisionNumber]);

  const handleDelete = useCallback(async () => {
    if (quoteNumber == null || revisionNumber == null || !deleting) return;
    setDeleteBusy(true);
    try {
      await quoteDocumentsService.remove(quoteNumber, revisionNumber, deleting.mapRecno);
      setDeleting(null);
      refreshList();
    } catch (e) {
      setUploadError(createErrorMessage(e, `Failed to delete ${deleting.filename}.`));
    } finally {
      setDeleteBusy(false);
    }
  }, [quoteNumber, revisionNumber, deleting, refreshList]);

  if (!saved) {
    return (
      <Card icon={<DocumentIcon className="h-3.5 w-3.5" />} title="Documents">
        <div className="px-3 py-10 text-center text-[0.78125rem] text-slate-500 dark:text-slate-400">
          Save the quote before uploading documents.
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Card icon={<DocumentIcon className="h-3.5 w-3.5" />} title="Upload Documents">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Document Type
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                aria-label="Document type"
                className="h-7 min-w-[180px] rounded border border-slate-300 bg-white px-2 text-[0.78125rem] font-normal normal-case text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              >
                {docTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Description
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                aria-label="Document description"
                className="h-7 w-full rounded border border-slate-300 bg-white px-2 text-[0.78125rem] font-normal normal-case text-slate-800 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
          </div>

          <FileDropzone<QuoteDocumentRow>
            onUpload={onUpload}
            onUploaded={() => refreshList()}
          />

          {uploadError && (
            <div role="alert" className="text-[0.78125rem] text-red-600 dark:text-red-400">{uploadError}</div>
          )}
        </div>
      </Card>

      <Card icon={<DocumentIcon className="h-3.5 w-3.5" />} title="Uploaded Documents" flushBody>
        {loading && (
          <div className="px-6 py-10 text-center text-[0.78125rem] text-slate-400 dark:text-slate-500">Loading documents…</div>
        )}
        {!loading && listError && (
          <div className="px-6 py-6 text-center text-[0.78125rem] text-red-600 dark:text-red-400">{listError}</div>
        )}
        {!loading && !listError && rows.length === 0 && (
          <div className="px-6 py-16 text-center text-[0.8125rem] text-slate-400 dark:text-slate-500">
            No documents uploaded yet.
          </div>
        )}
        {!loading && !listError && rows.length > 0 && (
          <>
            {presentTypes.length > 1 && (
              <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-2 dark:border-slate-700">
                <span className="text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Filter</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by type"
                  className="h-6 rounded border border-slate-300 bg-white px-1.5 text-[0.75rem] text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">All types</option>
                  {presentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">{visibleRows.length} of {rows.length}</span>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[0.75rem]">
                <thead>
                  <tr>
                    {['File', 'Type', 'Description', 'Uploaded', 'By', ''].map((h, i) => (
                      <th
                        key={h || `actions-${i}`}
                        className="bg-slate-100 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-left text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.mapRecno} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 font-medium text-slate-900 dark:text-slate-100">
                        {row.filename || '—'}
                      </td>
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {row.documentType || '—'}
                      </td>
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 text-slate-600 dark:text-slate-300 max-w-[260px] truncate" title={row.description}>
                        {row.description || <span className="text-slate-300 dark:text-slate-600">—</span>}
                      </td>
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 text-[0.71875rem] text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {row.uploadDatetime || '—'}
                      </td>
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {row.uploadedBy || '—'}
                      </td>
                      <td className="border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5 whitespace-nowrap text-right">
                        <div className="inline-flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => setPreviewing(row)}
                            aria-label={`Preview ${row.filename}`}
                            className="inline-flex h-6 items-center gap-1 rounded border border-slate-300 bg-white px-2 text-[0.71875rem] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(row)}
                            aria-label={`Edit ${row.filename}`}
                            className="inline-flex h-6 items-center gap-1 rounded border border-slate-300 bg-white px-2 text-[0.71875rem] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload(row)}
                            aria-label={`Download ${row.filename}`}
                            className="inline-flex h-6 items-center gap-1 rounded border border-slate-300 bg-white px-2 text-[0.71875rem] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                          >
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(row)}
                            aria-label={`Delete ${row.filename}`}
                            className="inline-flex h-6 items-center gap-1 rounded border border-red-300 bg-white px-2 text-[0.71875rem] font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {previewing && quoteNumber != null && revisionNumber != null && (
        <QuoteDocPreviewModal
          doc={previewing}
          quoteNumber={quoteNumber}
          revisionNumber={revisionNumber}
          onClose={() => setPreviewing(null)}
        />
      )}
      {editing && quoteNumber != null && revisionNumber != null && (
        <QuoteDocEditDialog
          doc={editing}
          quoteNumber={quoteNumber}
          revisionNumber={revisionNumber}
          docTypes={docTypes}
          onClose={() => setEditing(null)}
          onSaved={() => refreshList()}
        />
      )}
      {deleting && (
        <ConfirmDeleteModal
          filename={deleting.filename}
          busy={deleteBusy}
          onCancel={() => { if (!deleteBusy) setDeleting(null); }}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default DocumentsTab;
